// src/components/admin/AdminPanel.jsx
import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase, uploadImage, deleteImage } from '../../lib/supabase';

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  wrap:        { minHeight: '100vh', background: '#f4f6fb', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  topbar:      { background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 2rem', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 },
  topbarTitle: { color: '#4259A8', fontWeight: 700, fontSize: '1.1rem' },
  logoutBtn:   { background: 'none', border: '1px solid #dee2e6', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', color: '#6c757d', fontSize: '0.85rem' },
  layout:      { display: 'flex', minHeight: 'calc(100vh - 60px)' },
  sidebar:     { width: '220px', background: '#fff', borderRight: '1px solid #e2e8f0', padding: '1.5rem 0', flexShrink: 0 },
  navItem:     (active) => ({ display: 'block', width: '100%', textAlign: 'left', padding: '10px 1.5rem', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: active ? 600 : 400, color: active ? '#4259A8' : '#495057', background: active ? 'rgba(66,89,168,0.08)' : 'none', borderLeft: active ? '3px solid #4259A8' : '3px solid transparent' }),
  main:        { flex: 1, padding: '2rem', maxWidth: '900px' },
  card:        { background: '#fff', borderRadius: '10px', padding: '1.75rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardTitle:   { color: '#4259A8', fontWeight: 700, fontSize: '1rem', marginBottom: '1.25rem', paddingBottom: '0.75rem', borderBottom: '1px solid #f0f0f0' },
  label:       { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#6c757d', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input:       { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: '7px', fontSize: '0.92rem', color: '#333', boxSizing: 'border-box', marginBottom: '1rem' },
  textarea:    { width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: '7px', fontSize: '0.92rem', color: '#333', boxSizing: 'border-box', marginBottom: '1rem', resize: 'vertical', minHeight: '100px' },
  saveBtn:     { background: 'linear-gradient(135deg, #4259A8 0%, #2E8DD7 100%)', border: 'none', borderRadius: '7px', color: '#fff', fontWeight: 600, padding: '9px 22px', cursor: 'pointer', fontSize: '0.9rem' },
  addBtn:      { background: '#4259A8', border: 'none', borderRadius: '7px', color: '#fff', fontWeight: 600, padding: '8px 18px', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '1rem' },
  deleteBtn:   { background: 'none', border: '1px solid #dc3545', borderRadius: '6px', color: '#dc3545', padding: '5px 12px', cursor: 'pointer', fontSize: '0.8rem' },
  toast:       (ok) => ({ position: 'fixed', bottom: '1.5rem', right: '1.5rem', background: ok ? '#198754' : '#dc3545', color: '#fff', padding: '10px 20px', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', zIndex: 9999 }),
  row:         { display: 'flex', gap: '1rem', alignItems: 'flex-start' },
  half:        { flex: 1 },
};

// ─── Shared components ────────────────────────────────────────────────────────

function Toast({ msg, ok }) {
  if (!msg) return null;
  return <div style={s.toast(ok)}>{msg}</div>;
}

function Field({ label, value, onChange, type = 'text', rows }) {
  return (
    <div>
      <label style={s.label}>{label}</label>
      {rows
        ? <textarea value={value ?? ''} onChange={(e) => onChange(e.target.value)} rows={rows} style={s.textarea} />
        : <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} style={s.input} />}
    </div>
  );
}

// Image upload field — shows preview, upload button, delete button
function ImageField({ label, value, onChange, toast }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const isUrl = value && value.startsWith('http');
  const previewSrc = isUrl ? value : (value ? `/assets/${value.replace(/^\/assets\//, '').replace(/^assets\//, '')}` : null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      onChange(url);
      toast(true, 'Slika uploadana!');
    } catch (err) {
      toast(false, `Greška: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Obriši sliku?')) return;
    if (isUrl) {
      try {
        await deleteImage(value);
      } catch {
        // ignore storage delete errors
      }
    }
    onChange('');
  };

  return (
    <div style={{ marginBottom: '1rem' }}>
      <label style={s.label}>{label}</label>

      {/* Preview */}
      {previewSrc && (
        <div style={{ marginBottom: '0.5rem', position: 'relative', display: 'inline-block' }}>
          <img
            src={previewSrc}
            alt="preview"
            style={{ width: '120px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'block' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      {/* URL display */}
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="URL slike ili naziv datoteke"
        style={{ ...s.input, marginBottom: '0.5rem', fontSize: '0.8rem', color: '#6c757d' }}
      />

      {/* Buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button
          style={{ ...s.addBtn, marginBottom: 0, fontSize: '0.8rem', padding: '6px 14px', opacity: uploading ? 0.7 : 1 }}
          onClick={() => inputRef.current.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploadam...' : '↑ Upload slike'}
        </button>
        {value && (
          <button style={{ ...s.deleteBtn, fontSize: '0.8rem' }} onClick={handleDelete}>
            Ukloni sliku
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
      </div>
    </div>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────
function AboutEditor({ toast }) {
  const [blocks, setBlocks] = useState([]);

  useEffect(() => {
    supabase.from('about').select('*').order('block').then(({ data }) => setBlocks(data ?? []));
  }, []);

  const update = (idx, key, val) => setBlocks((prev) => prev.map((b, i) => i === idx ? { ...b, [key]: val } : b));

  const save = async (block) => {
    const { error } = await supabase.from('about').update({ image_url: block.image_url, body: block.body }).eq('id', block.id);
    toast(!error, error ? error.message : 'Spremljeno!');
  };

  return (
    <>
      {blocks.map((block, i) => (
        <div key={block.id} style={s.card}>
          <div style={s.cardTitle}>Blok {block.block}</div>
          <ImageField label="Slika" value={block.image_url} onChange={(v) => update(i, 'image_url', v)} toast={toast} />
          <Field label="Tekst" value={block.body} onChange={(v) => update(i, 'body', v)} rows={5} />
          <button style={s.saveBtn} onClick={() => save(block)}>Spremi</button>
        </div>
      ))}
    </>
  );
}

// ─── Gallery ──────────────────────────────────────────────────────────────────
function GalleryEditor({ toast }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    supabase.from('gallery').select('*').order('sort_order').then(({ data }) => setItems(data ?? []));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (id, key, val) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, [key]: val } : it));

  const save = async (item) => {
    const { error } = await supabase.from('gallery').update({ filename: item.filename, alt: item.alt, sort_order: item.sort_order }).eq('id', item.id);
    toast(!error, error ? error.message : 'Spremljeno!');
  };

  const add = async () => {
    const maxOrder = items.reduce((m, it) => Math.max(m, it.sort_order), 0);
    const { error } = await supabase.from('gallery').insert({ filename: '', alt: 'Galerija slika', sort_order: maxOrder + 1 });
    toast(!error, error ? error.message : 'Dodano!');
    load();
  };

  const remove = async (item) => {
    if (!window.confirm('Obriši sliku?')) return;
    await deleteImage(item.filename);
    const { error } = await supabase.from('gallery').delete().eq('id', item.id);
    toast(!error, error ? error.message : 'Obrisano.');
    load();
  };

  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Galerija slika</div>
      <button style={s.addBtn} onClick={add}>+ Dodaj sliku</button>
      {items.map((item) => (
        <div key={item.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1rem', marginBottom: '1rem' }}>
          <div style={s.row}>
            <div style={s.half}>
              <ImageField label="Slika" value={item.filename} onChange={(v) => update(item.id, 'filename', v)} toast={toast} />
            </div>
            <div style={s.half}>
              <Field label="Alt tekst" value={item.alt} onChange={(v) => update(item.id, 'alt', v)} />
              <Field label="Redoslijed" value={item.sort_order} type="number" onChange={(v) => update(item.id, 'sort_order', +v)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={s.saveBtn} onClick={() => save(item)}>Spremi</button>
            <button style={s.deleteBtn} onClick={() => remove(item)}>Obriši</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Team ─────────────────────────────────────────────────────────────────────
function TeamEditor({ toast }) {
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    supabase.from('team').select('*').order('sort_order').then(({ data }) => setMembers(data ?? []));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (id, key, val) => setMembers((prev) => prev.map((m) => m.id === id ? { ...m, [key]: val } : m));

  const updateArr = (id, key, idx, val) =>
    setMembers((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const arr = [...(m[key] ?? [])];
      arr[idx] = val;
      return { ...m, [key]: arr };
    }));

  const addArrItem = (id, key) =>
    setMembers((prev) => prev.map((m) => m.id === id ? { ...m, [key]: [...(m[key] ?? []), ''] } : m));

  const removeArrItem = (id, key, idx) =>
    setMembers((prev) => prev.map((m) => {
      if (m.id !== id) return m;
      const arr = [...(m[key] ?? [])];
      arr.splice(idx, 1);
      return { ...m, [key]: arr };
    }));

  const save = async (member) => {
    const { id, ...rest } = member;
    const { error } = await supabase.from('team').update(rest).eq('id', id);
    toast(!error, error ? error.message : 'Spremljeno!');
  };

  const add = async () => {
    const { error } = await supabase.from('team').insert({ slug: 'novi-clan', name: 'Novi član', role: 'Uloga', image: '', sort_order: members.length + 1 });
    toast(!error, error ? error.message : 'Član dodan!');
    load();
  };

  const remove = async (member) => {
    if (!window.confirm('Obriši člana?')) return;
    await deleteImage(member.image);
    const { error } = await supabase.from('team').delete().eq('id', member.id);
    toast(!error, error ? error.message : 'Obrisano.');
    load();
  };

  return (
    <>
      <button style={s.addBtn} onClick={add}>+ Dodaj člana tima</button>
      {members.map((m) => (
        <div key={m.id} style={s.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <button style={{ background: 'none', border: 'none', fontWeight: 700, color: '#4259A8', cursor: 'pointer', fontSize: '1rem', padding: 0 }} onClick={() => setOpen(open === m.id ? null : m.id)}>
              {m.name} — {m.role} {open === m.id ? '▲' : '▼'}
            </button>
            <button style={s.deleteBtn} onClick={() => remove(m)}>Obriši</button>
          </div>

          {open === m.id && (
            <>
              <div style={s.row}>
                <div style={s.half}><Field label="Slug (URL)" value={m.slug} onChange={(v) => update(m.id, 'slug', v)} /></div>
                <div style={s.half}><Field label="Redoslijed" value={m.sort_order} type="number" onChange={(v) => update(m.id, 'sort_order', +v)} /></div>
              </div>
              <div style={s.row}>
                <div style={s.half}><Field label="Ime i prezime" value={m.name} onChange={(v) => update(m.id, 'name', v)} /></div>
                <div style={s.half}><Field label="Uloga" value={m.role} onChange={(v) => update(m.id, 'role', v)} /></div>
              </div>
              <ImageField label="Foto" value={m.image} onChange={(v) => update(m.id, 'image', v)} toast={toast} />
              <Field label="Kratki bio (kartica)" value={m.bio} onChange={(v) => update(m.id, 'bio', v)} rows={2} />
              <Field label="Dugi bio" value={m.long_bio} onChange={(v) => update(m.id, 'long_bio', v)} rows={4} />
              <Field label="Obrazovanje" value={m.education_text} onChange={(v) => update(m.id, 'education_text', v)} rows={3} />
              <Field label="Pristup radu" value={m.approach} onChange={(v) => update(m.id, 'approach', v)} rows={3} />

              <label style={s.label}>Specijalizacije</label>
              {(m.specializations ?? []).map((spec, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem' }}>
                  <input value={spec} onChange={(e) => updateArr(m.id, 'specializations', i, e.target.value)} style={{ ...s.input, marginBottom: 0, flex: 1 }} />
                  <button style={s.deleteBtn} onClick={() => removeArrItem(m.id, 'specializations', i)}>×</button>
                </div>
              ))}
              <button style={{ ...s.addBtn, fontSize: '0.78rem', padding: '5px 12px', marginBottom: '1rem' }} onClick={() => addArrItem(m.id, 'specializations')}>+ Dodaj specijalizaciju</button>

              {m.slug === 'dejan' && (
                <>
                  <Field label="Karijera — početak" value={m.career_start} onChange={(v) => update(m.id, 'career_start', v)} rows={3} />
                  <Field label="Sportsko iskustvo" value={m.sports_experience} onChange={(v) => update(m.id, 'sports_experience', v)} rows={3} />
                  <Field label="Edukacija i volonterstvo" value={m.education_and_volunteering} onChange={(v) => update(m.id, 'education_and_volunteering', v)} rows={3} />
                  <Field label="Volonterstvo" value={m.volunteering} onChange={(v) => update(m.id, 'volunteering', v)} rows={3} />
                  <Field label="Priznanja" value={m.awards} onChange={(v) => update(m.id, 'awards', v)} rows={3} />
                  <Field label="Osnivanje Poliklinike Akrion" value={m.akrion_foundation} onChange={(v) => update(m.id, 'akrion_foundation', v)} rows={3} />
                  <Field label="Vizija" value={m.vision} onChange={(v) => update(m.id, 'vision', v)} rows={3} />
                  <Field label="Vrijednosti" value={m.values} onChange={(v) => update(m.id, 'values', v)} rows={3} />
                </>
              )}

              <button style={s.saveBtn} onClick={() => save(m)}>Spremi promjene</button>
            </>
          )}
        </div>
      ))}
    </>
  );
}

// ─── Clients ──────────────────────────────────────────────────────────────────
function ClientsEditor({ toast }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    supabase.from('clients').select('*').order('sort_order').then(({ data }) => setItems(data ?? []));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (id, key, val) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, [key]: val } : it));

  const save = async (item) => {
    const { id, ...rest } = item;
    const { error } = await supabase.from('clients').update(rest).eq('id', id);
    toast(!error, error ? error.message : 'Spremljeno!');
  };

  const add = async () => {
    const { error } = await supabase.from('clients').insert({ name: 'Novi klijent', logo: '', link: '#!', sort_order: items.length + 1 });
    toast(!error, error ? error.message : 'Dodano!');
    load();
  };

  const remove = async (item) => {
    if (!window.confirm('Obriši klijenta?')) return;
    await deleteImage(item.logo);
    await supabase.from('clients').delete().eq('id', item.id);
    load();
  };

  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Klijenti / Partneri</div>
      <button style={s.addBtn} onClick={add}>+ Dodaj klijenta</button>
      {items.map((item) => (
        <div key={item.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1rem', marginBottom: '1rem' }}>
          <div style={s.row}>
            <div style={s.half}><Field label="Naziv" value={item.name} onChange={(v) => update(item.id, 'name', v)} /></div>
            <div style={s.half}><Field label="Link" value={item.link} onChange={(v) => update(item.id, 'link', v)} /></div>
          </div>
          <ImageField label="Logo" value={item.logo} onChange={(v) => update(item.id, 'logo', v)} toast={toast} />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={s.saveBtn} onClick={() => save(item)}>Spremi</button>
            <button style={s.deleteBtn} onClick={() => remove(item)}>Obriši</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Blog News ────────────────────────────────────────────────────────────────
function BlogNewsEditor({ toast }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    supabase.from('blog_news').select('*').order('sort_order').then(({ data }) => setItems(data ?? []));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (id, key, val) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, [key]: val } : it));

  const save = async (item) => {
    const { id, ...rest } = item;
    const { error } = await supabase.from('blog_news').update(rest).eq('id', id);
    toast(!error, error ? error.message : 'Spremljeno!');
  };

  const add = async () => {
    const { error } = await supabase.from('blog_news').insert({ image_url: '', title: 'Novi članak', description: '', article_url: 'https://', sort_order: items.length + 1 });
    toast(!error, error ? error.message : 'Dodano!');
    load();
  };

  const remove = async (item) => {
    if (!window.confirm('Obriši članak?')) return;
    await deleteImage(item.image_url);
    await supabase.from('blog_news').delete().eq('id', item.id);
    load();
  };

  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Blog — Novosti</div>
      <button style={s.addBtn} onClick={add}>+ Dodaj članak</button>
      {items.map((item) => (
        <div key={item.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1rem', marginBottom: '1rem' }}>
          <ImageField label="Slika" value={item.image_url} onChange={(v) => update(item.id, 'image_url', v)} toast={toast} />
          <Field label="Naslov" value={item.title} onChange={(v) => update(item.id, 'title', v)} />
          <Field label="Opis" value={item.description} onChange={(v) => update(item.id, 'description', v)} rows={2} />
          <div style={s.row}>
            <div style={s.half}><Field label="URL članka" value={item.article_url} onChange={(v) => update(item.id, 'article_url', v)} /></div>
            <div style={{ width: '80px' }}><Field label="Redoslijed" value={item.sort_order} type="number" onChange={(v) => update(item.id, 'sort_order', +v)} /></div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={s.saveBtn} onClick={() => save(item)}>Spremi</button>
            <button style={s.deleteBtn} onClick={() => remove(item)}>Obriši</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Blog Audio ───────────────────────────────────────────────────────────────
function BlogAudioEditor({ toast }) {
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    supabase.from('blog_audio').select('*').order('sort_order').then(({ data }) => setItems(data ?? []));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (id, key, val) => setItems((prev) => prev.map((it) => it.id === id ? { ...it, [key]: val } : it));

  const save = async (item) => {
    const { id, ...rest } = item;
    const { error } = await supabase.from('blog_audio').update(rest).eq('id', id);
    toast(!error, error ? error.message : 'Spremljeno!');
  };

  const add = async () => {
    const { error } = await supabase.from('blog_audio').insert({ image_url: '', title: 'Novi zvučni zapis', description: '', audio_url: 'https://', duration: '25 min', sort_order: items.length + 1 });
    toast(!error, error ? error.message : 'Dodano!');
    load();
  };

  const remove = async (item) => {
    if (!window.confirm('Obriši zapis?')) return;
    await deleteImage(item.image_url);
    await supabase.from('blog_audio').delete().eq('id', item.id);
    load();
  };

  return (
    <div style={s.card}>
      <div style={s.cardTitle}>Blog — Zvučni zapisi</div>
      <button style={s.addBtn} onClick={add}>+ Dodaj zapis</button>
      {items.map((item) => (
        <div key={item.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '1rem', marginBottom: '1rem' }}>
          <ImageField label="Slika" value={item.image_url} onChange={(v) => update(item.id, 'image_url', v)} toast={toast} />
          <Field label="Naslov" value={item.title} onChange={(v) => update(item.id, 'title', v)} />
          <Field label="Opis" value={item.description} onChange={(v) => update(item.id, 'description', v)} rows={2} />
          <div style={s.row}>
            <div style={s.half}><Field label="URL zvučnog zapisa" value={item.audio_url} onChange={(v) => update(item.id, 'audio_url', v)} /></div>
            <div style={{ width: '120px' }}><Field label="Trajanje" value={item.duration} onChange={(v) => update(item.id, 'duration', v)} /></div>
            <div style={{ width: '80px' }}><Field label="Redoslijed" value={item.sort_order} type="number" onChange={(v) => update(item.id, 'sort_order', +v)} /></div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button style={s.saveBtn} onClick={() => save(item)}>Spremi</button>
            <button style={s.deleteBtn} onClick={() => remove(item)}>Obriši</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Services ─────────────────────────────────────────────────────────────────
function ServicesEditor({ toast }) {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    supabase.from('services').select('*').order('slug').then(({ data }) => setServices(data ?? []));
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (id, key, val) => setServices((prev) => prev.map((sv) => sv.id === id ? { ...sv, [key]: val } : sv));
  const updateIntro = (id, val) => update(id, 'intro', val.split('\n').filter(Boolean));
  const updateSections = (id, val) => {
    try { update(id, 'sections', JSON.parse(val)); } catch { /* mid-edit */ }
  };

  const save = async (sv) => {
    const { id, ...rest } = sv;
    const { error } = await supabase.from('services').update(rest).eq('id', id);
    toast(!error, error ? error.message : 'Spremljeno!');
  };

  return (
    <>
      <div style={{ ...s.card, background: 'rgba(66,89,168,0.06)', border: '1px solid rgba(66,89,168,0.15)' }}>
        <p style={{ margin: 0, fontSize: '0.85rem', color: '#4259A8' }}>
          <strong>Napomena:</strong> Sekcije (kartice) uređujete kao JSON. Svaki objekt u nizu predstavlja jednu karticu na stranici.
        </p>
      </div>
      {services.map((sv) => (
        <div key={sv.id} style={s.card}>
          <button style={{ background: 'none', border: 'none', fontWeight: 700, color: '#4259A8', cursor: 'pointer', fontSize: '1rem', marginBottom: open === sv.id ? '1rem' : 0 }} onClick={() => setOpen(open === sv.id ? null : sv.id)}>
            {sv.title} ({sv.slug}) {open === sv.id ? '▲' : '▼'}
          </button>

          {open === sv.id && (
            <>
              <div style={s.row}>
                <div style={s.half}><Field label="Naslov (h1)" value={sv.title} onChange={(v) => update(sv.id, 'title', v)} /></div>
                <div style={s.half}><Field label="Podnaslov" value={sv.subtitle} onChange={(v) => update(sv.id, 'subtitle', v)} /></div>
              </div>
              <ImageField label="Slika pozadine (hero)" value={sv.image_url} onChange={(v) => update(sv.id, 'image_url', v)} toast={toast} />
              <Field label="Pozicija slike (center/bottom/top)" value={sv.bg_position} onChange={(v) => update(sv.id, 'bg_position', v)} />

              <label style={s.label}>Uvodni tekst (svaki red = novi odlomak)</label>
              <textarea value={(sv.intro ?? []).join('\n')} onChange={(e) => updateIntro(sv.id, e.target.value)} rows={5} style={s.textarea} />

              <label style={s.label}>Sekcije (JSON)</label>
              <textarea defaultValue={JSON.stringify(sv.sections, null, 2)} onChange={(e) => updateSections(sv.id, e.target.value)} rows={20} style={{ ...s.textarea, fontFamily: 'monospace', fontSize: '0.8rem' }} />

              <div style={s.row}>
                <div style={s.half}><Field label="SEO naslov" value={sv.seo_title} onChange={(v) => update(sv.id, 'seo_title', v)} /></div>
                <div style={s.half}><Field label="SEO canonical URL" value={sv.seo_canonical} onChange={(v) => update(sv.id, 'seo_canonical', v)} /></div>
              </div>
              <Field label="SEO opis" value={sv.seo_description} onChange={(v) => update(sv.id, 'seo_description', v)} rows={2} />
              <Field label="SEO ključne riječi" value={sv.seo_keywords} onChange={(v) => update(sv.id, 'seo_keywords', v)} />

              <button style={s.saveBtn} onClick={() => save(sv)}>Spremi promjene</button>
            </>
          )}
        </div>
      ))}
    </>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────
const SECTIONS = [
  { key: 'about',      label: 'O nama' },
  { key: 'gallery',    label: 'Galerija' },
  { key: 'team',       label: 'Tim' },
  { key: 'clients',    label: 'Klijenti' },
  { key: 'blog-news',  label: 'Blog — Novosti' },
  { key: 'blog-audio', label: 'Blog — Zvuk' },
  { key: 'services',   label: 'Usluge' },
];

export default function AdminPanel() {
  const [active, setActive] = useState('about');
  const [toastMsg, setToastMsg] = useState('');
  const [toastOk, setToastOk] = useState(true);

  const toast = (ok, msg) => {
    setToastOk(ok);
    setToastMsg(msg);
    setTimeout(() => setToastMsg(''), 3000);
  };

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/admin/login';
  };

  const renderSection = () => {
    switch (active) {
      case 'about':      return <AboutEditor toast={toast} />;
      case 'gallery':    return <GalleryEditor toast={toast} />;
      case 'team':       return <TeamEditor toast={toast} />;
      case 'clients':    return <ClientsEditor toast={toast} />;
      case 'blog-news':  return <BlogNewsEditor toast={toast} />;
      case 'blog-audio': return <BlogAudioEditor toast={toast} />;
      case 'services':   return <ServicesEditor toast={toast} />;
      default:           return null;
    }
  };

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <span style={s.topbarTitle}>⚕ Poliklinika Akrion — Admin</span>
        <button style={s.logoutBtn} onClick={logout}>Odjava</button>
      </div>
      <div style={s.layout}>
        <nav style={s.sidebar}>
          {SECTIONS.map((sec) => (
            <button key={sec.key} style={s.navItem(active === sec.key)} onClick={() => setActive(sec.key)}>
              {sec.label}
            </button>
          ))}
        </nav>
        <main style={s.main}>
          <h2 style={{ color: '#4259A8', fontWeight: 700, marginBottom: '1.5rem', fontSize: '1.3rem' }}>
            {SECTIONS.find((sec) => sec.key === active)?.label}
          </h2>
          {renderSection()}
        </main>
      </div>
      <Toast msg={toastMsg} ok={toastOk} />
    </div>
  );
}